import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import concesionariaWarehouseConfigService from '../../../services/concesionariaWarehouseConfigService';

const prettyModuleName = (raw) => {
  if (!raw) return '—';
  const txt = String(raw).replace(/[_-]+/g, ' ').trim();
  return txt.charAt(0).toUpperCase() + txt.slice(1);
};

const AlmacenesProductoTerminado = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [availablePrincipals, setAvailablePrincipals] = useState([]);
  const [principalId, setPrincipalId] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await concesionariaWarehouseConfigService.list();
      if (res?.success) {
        setConfigs(Array.isArray(res.data?.configs) ? res.data.configs : []);
        setAvailablePrincipals(Array.isArray(res.data?.available_principals) ? res.data.available_principals : []);
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'No se pudo cargar la configuración de almacenes PT.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const createConfig = async (e) => {
    e.preventDefault();
    if (!principalId) {
      setToast({ isOpen: true, message: 'Seleccione un almacén principal.', type: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const res = await concesionariaWarehouseConfigService.create(principalId);
      if (res?.success) {
        setToast({ isOpen: true, message: res.message || 'Configuración guardada.', type: 'success' });
        setPrincipalId('');
        await loadData();
      } else {
        setToast({ isOpen: true, message: res?.message || 'No se pudo guardar.', type: 'error' });
      }
    } catch (e2) {
      const msg = e2.response?.data?.message || 'No se pudo crear la configuración.';
      setToast({ isOpen: true, message: msg, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const removeConfig = async () => {
    if (!deleteTarget) return;
    try {
      const res = await concesionariaWarehouseConfigService.remove(deleteTarget.id);
      if (res?.success) {
        setToast({ isOpen: true, message: res.message || 'Configuración eliminada.', type: 'success' });
        setDeleteTarget(null);
        await loadData();
      } else {
        setToast({ isOpen: true, message: res?.message || 'No se pudo eliminar.', type: 'error' });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'No se pudo eliminar la configuración.',
        type: 'error',
      });
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full max-w-[1600px] mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">
          Dashboard
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria" className="hover:text-teal-600">
          Producción concesionaria
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria/configuraciones" className="hover:text-teal-600">
          Configuración
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Almacenes producto terminado</span>
      </nav>

      <h1 className="text-2xl font-semibold text-gray-800">Almacenes de Producto Terminado</h1>
      <p className="mt-1 text-sm text-gray-500">
        Seleccione un almacén principal y el sistema creará automáticamente su almacén de producto terminado para el
        módulo concesionaria.
      </p>

      <form
        onSubmit={createConfig}
        className="mt-5 rounded-lg border border-gray-200 bg-white p-4 shadow-sm flex flex-col md:flex-row md:items-end gap-3"
      >
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Almacén principal</label>
          <select
            value={principalId}
            onChange={(e) => setPrincipalId(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500 bg-white"
            disabled={saving || availablePrincipals.length === 0}
          >
            <option value="">Seleccione...</option>
            {availablePrincipals.map((w) => (
              <option key={w.id} value={w.id}>
                {w.code ? `${w.code} - ${w.name}` : w.name}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          disabled={saving || !principalId}
          className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Creando…' : 'Crear almacén PT'}
        </button>
      </form>

      <div className="mt-6 rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden w-full">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">Cargando configuraciones…</div>
        ) : configs.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">Aún no hay configuraciones de almacén principal → producto terminado.</div>
        ) : (
          <div className="overflow-x-auto w-full">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <th className="px-3 py-2">Almacén principal</th>
                  <th className="px-3 py-2">Almacén producto terminado</th>
                  <th className="px-3 py-2">Módulo</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {configs.map((cfg) => (
                  <tr key={cfg.id}>
                    <td className="px-3 py-2">
                      {(cfg.principal_warehouse?.code || cfg.principalWarehouse?.code || '—')} -{' '}
                      {(cfg.principal_warehouse?.name || cfg.principalWarehouse?.name || '—')}
                    </td>
                    <td className="px-3 py-2">
                      {(cfg.finished_warehouse?.code || cfg.finishedWarehouse?.code || '—')} -{' '}
                      {(cfg.finished_warehouse?.name || cfg.finishedWarehouse?.name || '—')}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex rounded-full bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700 ring-1 ring-violet-200">
                        {prettyModuleName(cfg.finished_warehouse?.created_for_module || cfg.finishedWarehouse?.created_for_module)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          cfg.is_active ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {cfg.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(cfg)}
                        className="text-red-600 hover:text-red-800 text-xs font-medium"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={removeConfig}
        title="Eliminar configuración"
        message={`Se eliminará la configuración y el almacén PT asociado de «${deleteTarget?.principalWarehouse?.name || deleteTarget?.principal_warehouse?.name || ''}».`}
        confirmText="Eliminar"
        cancelText="Cancelar"
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
};

export default AlmacenesProductoTerminado;

